import { useAuth } from '@/auth/AuthProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PortalService } from '@/services/database/PortalService';
import {
  CreatePortalParams,
  UpdatePortalParams,
  AddGenieToPortalParams,
  UpdatePortalGenieParams,
} from '@/types/portal/portal';

// =====================
// Portal Queries
// =====================

export const usePortals = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['portals', user?.id],
    queryFn: () => PortalService.listPortals(user?.id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!user?.id,
  });
};

export const usePortal = (portalId: string) => {
  return useQuery({
    queryKey: ['portal', portalId],
    queryFn: () => PortalService.getPortal(portalId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!portalId,
  });
};

export const usePortalBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['portal-slug', slug],
    queryFn: () => PortalService.getPortalBySlug(slug),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!slug,
  });
};

export const usePortalGenies = (portalId: string) => {
  return useQuery({
    queryKey: ['portal-genies', portalId],
    queryFn: () => PortalService.getPortalGenies(portalId),
    staleTime: 5 * 60 * 1000,
    enabled: !!portalId,
  });
};

// =====================
// Portal Mutations
// =====================

export const useCreatePortal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreatePortalParams) => PortalService.createPortal(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
      toast.success('Portal created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create portal');
    },
  });
};

export const useUpdatePortal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ portalId, updates }: { portalId: string; updates: UpdatePortalParams }) =>
      PortalService.updatePortal(portalId, updates),
    onSuccess: (_, { portalId }) => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
      queryClient.invalidateQueries({ queryKey: ['portal', portalId] });
      toast.success('Portal updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update portal');
    },
  });
};

export const useDeletePortal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (portalId: string) => PortalService.deletePortal(portalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
      toast.success('Portal deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete portal');
    },
  });
};

export const useReorderPortals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (portalIds: string[]) => PortalService.reorderPortals(portalIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reorder portals');
    },
  });
};

// =====================
// Portal Genie Mutations
// =====================

export const useAddGenieToPortal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddGenieToPortalParams) => PortalService.addGenieToPortal(params),
    onSuccess: (_, { portal_id }) => {
      queryClient.invalidateQueries({ queryKey: ['portal-genies', portal_id] });
      queryClient.invalidateQueries({ queryKey: ['portal', portal_id] });
      toast.success('Genie added to portal');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add genie to portal');
    },
  });
};

export const useUpdatePortalGenie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      portalGenieId,
      portalId,
      updates,
    }: {
      portalGenieId: number;
      portalId: string;
      updates: UpdatePortalGenieParams;
    }) => PortalService.updatePortalGenie(portalGenieId, updates),
    onSuccess: (_, { portalId }) => {
      queryClient.invalidateQueries({ queryKey: ['portal-genies', portalId] });
      toast.success('Genie updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update genie');
    },
  });
};

export const useRemoveGenieFromPortal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ portalId, agentId }: { portalId: string; agentId: string }) =>
      PortalService.removeGenieFromPortal(portalId, agentId),
    onSuccess: (_, { portalId }) => {
      queryClient.invalidateQueries({ queryKey: ['portal-genies', portalId] });
      queryClient.invalidateQueries({ queryKey: ['portal', portalId] });
      toast.success('Genie removed from portal');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove genie');
    },
  });
};

export const useReorderPortalGenies = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ portalId, orderedAgentIds }: { portalId: string; orderedAgentIds: string[] }) =>
      PortalService.reorderPortalGenies(portalId, orderedAgentIds),
    onSuccess: (_, { portalId }) => {
      queryClient.invalidateQueries({ queryKey: ['portal-genies', portalId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reorder genies');
    },
  });
};
